import { palette } from '@app/src/theme/palette'
import { CountryVariant, RiskStatus, SustainabilityArea } from '@app/src/types/resourceExplorer'
import type {
  SustainabilityAreaRisk,
  SustainabilityTopicCountryRisk,
  SustainabilityTopicIndustryRisk,
  SustainabilityTopicRisk,
} from '@app/src/types/topicalRisk'
import { sortAlphabetically } from '@app/src/utils/helpers'
import type { Theme } from '@mui/material'
import { alpha, darken, decomposeColor, lighten } from '@mui/material'

export type GroupedCountryRisk = {
  variant: CountryVariant
  countries: string[]
}

// The risk group's ids too, so the risk table's own columns take the place of the group's filters.
export const OVERALL_RISK_STATUS_COLUMN_ID = 'overallRisk'

export const INHERENT_RISK_STATUS_COLUMN_ID = 'inherentRisk'

export const OVERALL_RISK_STATUS_FIELD = 'riskStatus'

export const RISK_STATUS_DESC_ORDER = [
  RiskStatus.Extreme,
  RiskStatus.High,
  RiskStatus.Medium,
  RiskStatus.Low,
  RiskStatus.NotApplicable,
]

/**
 * Maps CountryVariant enum values to their corresponding translation keys
 */
export function getCountryVariantTranslationKey(variant: CountryVariant): string {
  switch (variant) {
    case CountryVariant.Headquarter:
      return 'schemas.risk.riskLocations.headquarter'
    case CountryVariant.Operation:
      return 'schemas.risk.riskLocations.ownOperations'
    case CountryVariant.Subsupplier:
      return 'schemas.risk.riskLocations.subsupplier'
    case CountryVariant.Industry:
      return 'schemas.risk.indexType.Industry'
    default:
      return ''
  }
}

/**
 * Maps RiskStatus enum values to their corresponding translation keys (short form)
 * Returns: "Extreme", "High", "Medium", "Low", "No data", "Pending", "Not activated"
 */
export function getRiskStatusTranslationKey(risk?: RiskStatus): string {
  switch (risk) {
    case RiskStatus.Extreme:
      return 'schemas.risk.status.FExtreme'
    case RiskStatus.High:
      return 'schemas.risk.status.EHigh'
    case RiskStatus.Medium:
      return 'schemas.risk.status.DMedium'
    case RiskStatus.Low:
      return 'schemas.risk.status.CLow'
    case RiskStatus.Pending:
      return 'schemas.risk.status.BPending'
    case RiskStatus.Deactivated:
      return 'schemas.risk.status.GDeactivated'
    case RiskStatus.InsufficientData:
      return 'schemas.risk.status.HInsufficientData'
    case RiskStatus.NotApplicable:
    default:
      return 'schemas.risk.status.ANotApplicable' // "No data"
  }
}

export function getRiskStatusShortKey(risk?: RiskStatus): string {
  return getRiskStatusTranslationKey(risk)
}

/**
 * Returns the color for a given risk status (chip backgrounds, dots, chart segments, etc.)
 */
export function getRiskColor(risk?: RiskStatus): string {
  switch (risk) {
    case RiskStatus.Extreme:
      return palette.semantic.error
    case RiskStatus.High:
      return palette.error.main
    case RiskStatus.Medium:
      return palette.warning.main
    case RiskStatus.Low:
      return palette.success.main
    case RiskStatus.NotApplicable:
      return palette.action.disabledBackground
    case RiskStatus.InsufficientData:
    default:
      return 'transparent'
  }
}

export function getRiskTextContrastColor(riskStatus: RiskStatus) {
  return riskStatus === RiskStatus.Extreme ? palette.primary.contrastText : palette.primary.main
}

/**
 * Returns a weaker/lighter color for a given  risk status
 */
export function getRiskWeakerColor(theme: Theme, risk?: RiskStatus): string {
  switch (risk) {
    case RiskStatus.Extreme:
      return lighten(palette.semantic.error, 0.9)
    case RiskStatus.High:
      return palette.error.light
    case RiskStatus.Medium:
      return palette.warning.light
    case RiskStatus.Low:
      return palette.success.light
    case RiskStatus.NotApplicable:
      return getRiskColor(RiskStatus.NotApplicable)
    default:
      return theme.palette.grey[200]
  }
}

/**
 * Returns a stronger/darker color for a given  risk status
 */
export function getRiskStrongerColor(risk?: RiskStatus): string {
  switch (risk) {
    case RiskStatus.Extreme:
      return darken(palette.semantic.error, 0.3)
    case RiskStatus.High:
      return palette.error.dark
    case RiskStatus.Medium:
      return palette.warning.dark
    case RiskStatus.Low:
      return palette.success.dark
    case RiskStatus.NotApplicable:
    default:
      return 'transparent'
  }
}

/**
 * Returns the secondary text color for a given  risk status
 */
export function getRiskTextSecondaryColor(theme: Theme, risk?: RiskStatus): string {
  switch (risk) {
    case RiskStatus.Extreme:
    case RiskStatus.High:
    case RiskStatus.Medium:
    case RiskStatus.Low: {
      const { type, values } = decomposeColor(theme.palette.text.secondary)
      const textSecondaryAlpha = (type === 'rgba' ? values[3] : 1) ?? 0.6
      return alpha(darken(getRiskColor(risk), 0.8), textSecondaryAlpha)
    }
    case RiskStatus.NotApplicable:
    default:
      return theme.palette.text.secondary
  }
}

/**
 * Gets assessment data from a sustainability topic risk
 */
export function getAssessmentData(risk: SustainabilityTopicRisk) {
  // Assessment level takes precedence over section level (1st prio and 2nd prio)

  if (risk.assessmentLevel) {
    return {
      level: risk.assessmentLevel,
      text: risk.assessmentTemplate?.name,
      assessmentTemplateId: risk.assessmentTemplateId,
    }
  }

  if (risk.assessmentSectionLevel) {
    const section = risk.assessmentTemplateSection
    return {
      level: risk.assessmentSectionLevel,
      text: section ? `${section.assessmentTemplate?.name || ''}: ${section.name || ''}` : undefined,
      assessmentTemplateId: section?.assessmentTemplateId,
    }
  }

  return {}
}

/**
 * Gets relevant country risks grouped by variant and filtered by risk status and sorted alphabetically
 */
export function getRelevantCountryRisks(
  countryRisks: SustainabilityTopicCountryRisk[] | undefined,
  countryRiskStatus: RiskStatus | undefined,
  getLocalisedCountryName: (countryCode?: string) => string,
): GroupedCountryRisk[] {
  if (!countryRisks) return []
  if (countryRiskStatus === undefined) return []

  const filteredRisks = countryRisks.filter(risk => risk.riskStatus === countryRiskStatus)

  const risksWithLocalizedNames = filteredRisks.map(risk => ({
    ...risk,
    localizedCountryName: getLocalisedCountryName(risk.country?.countryCode),
  }))

  const grouped = Object.groupBy(risksWithLocalizedNames, risk => risk.variant)
  const variants = [CountryVariant.Headquarter, CountryVariant.Operation]

  return variants
    .map(variant => {
      const risks = grouped[variant]
      if (!risks || risks.length === 0) return null

      return {
        variant,
        countries: sortAlphabetically(risks.map(r => r.localizedCountryName)),
      }
    })
    .filter((group): group is NonNullable<typeof group> => group !== null)
}

/**
 * Gets relevant industry risks filtered by risk status and sorted alphabetically
 */
export function getRelevantIndustryRisks(
  industryRisks: SustainabilityTopicIndustryRisk[] | undefined,
  industryRiskStatus: RiskStatus,
) {
  if (!industryRisks) return []

  const filteredRisks = industryRisks.filter(risk => risk.riskStatus === industryRiskStatus)

  return sortAlphabetically(
    filteredRisks.map(risk => ({
      name: risk.subNaceCode?.description || '',
      riskStatus: risk.riskStatus,
    })),
    item => item.name,
  )
}

export function ensureAllSustainabilityAreas(
  sustainabilityAreaRisks: SustainabilityAreaRisk[] = [],
): SustainabilityAreaRisk[] {
  const areaRiskMap = new Map(sustainabilityAreaRisks.map(risk => [risk.sustainabilityArea, risk]))
  return [
    SustainabilityArea.Environment,
    SustainabilityArea.Social,
    SustainabilityArea.Governance,
    SustainabilityArea.SupplyChain,
  ].map(
    area =>
      areaRiskMap.get(area) ?? {
        sustainabilityArea: area,
        sustainabilityTopicRisks: [],
      },
  ) as SustainabilityAreaRisk[]
}
